/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';
import { useContext, useEffect, useState } from 'react';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { localDateTimeToUtc } from 'apps/marsinet/src/app/utils/timezone-utils';

type EmployeeOption = { label: string; value: string };

interface AllocateTimeModalProps {
    visible: boolean;
    maintenanceOrderId: string;
    onHide: () => void;
    onSuccess?: () => void;
}

export function AllocateTimeModal({
    visible,
    maintenanceOrderId,
    onHide,
    onSuccess,
}: AllocateTimeModalProps) {
    const { t } = useTranslation();
    const { selectedTimezone } = useContext(MarsinetContext);

    const [initialDate, setInitialDate] = useState<Date | null>(null);
    const [finalDate, setFinalDate] = useState<Date | null>(null);
    const [comments, setComments] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [employees, setEmployees] = useState<EmployeeOption[]>([]);
    const [employee, setEmployee] = useState<string>('');

    useEffect(() => {
        if (!visible) return;

        const loadEmployees = async () => {
            try {
                const response =
                    await apiServices.userApi.usersControllerGetIncidentDetectedByOptions();
                const options = response?.data?.options ?? [];

                const employeeOptions = options.map((option: any) => ({
                    label: option.email,
                    value: option.email,
                }));

                const isMarsiOrganization =
                    response?.data?.is_marsi_organization ?? false;
                const organizationName = response?.data?.organization_name ?? '';
                const defaultDetectedBy = response?.data?.default_detected_by ?? '';

                if (!isMarsiOrganization && defaultDetectedBy) {
                    employeeOptions.unshift({
                        label: organizationName || defaultDetectedBy,
                        value: defaultDetectedBy,
                    });
                }

                setEmployees(employeeOptions);
            } catch (err) {
                console.error('[AllocateTimeModal] Error loading employees', err);
            }
        };

        loadEmployees();
    }, [visible]);

    const resetForm = () => {
        setInitialDate(null);
        setFinalDate(null);
        setComments('');
        setEmployee('');
        setError(null);
    };

    const handleHide = () => {
        if (submitting) {
            return;
        }
        resetForm();
        onHide();
    };

    // Calcula la duración con instantes UTC para respetar los cambios de zona y horario de verano.
    let durationMinutes: number | null = null;
    let dateConversionError: string | null = null;
    if (initialDate && finalDate) {
        try {
            const initialUtc = new Date(
                localDateTimeToUtc(initialDate, selectedTimezone)
            );
            const finalUtc = new Date(
                localDateTimeToUtc(finalDate, selectedTimezone)
            );
            durationMinutes = Math.round(
                (finalUtc.getTime() - initialUtc.getTime()) / 60000
            );
        } catch (error) {
            if (error instanceof RangeError) {
                dateConversionError = error.message;
            } else {
                throw error;
            }
        }
    }

    const isValid =
        !!employee &&
        !!initialDate &&
        !!finalDate &&
        durationMinutes !== null &&
        durationMinutes >= 0;

    const handleSubmit = async () => {
        if (!isValid || !initialDate || !finalDate) {
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            // La API guarda fechas UTC; convierte los dos valores antes de enviarlos.
            await apiServices.maintenanceOrderApi.maintenanceOrdersControllerAllocateTimeToMaintenanceOrder(
                {
                    assigned_employee: employee,
                    initial_date: new Date(
                        localDateTimeToUtc(initialDate, selectedTimezone)
                    ),
                    final_date: new Date(
                        localDateTimeToUtc(finalDate, selectedTimezone)
                    ),
                    comments: comments.trim() || undefined,
                },
                maintenanceOrderId
            );

            resetForm();
            onSuccess?.();
            onHide();
        } catch (err: any) {
            console.error('[AllocateTimeModal] Error allocating time', err);
            setError(
                err?.response?.data?.message ??
                t('pages.maintenance_orders.allocate_time_error')
            );
        } finally {
            setSubmitting(false);
        }
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button
                label={t('cancel')}
                icon="pi pi-times"
                severity="secondary"
                text
                onClick={handleHide}
                disabled={submitting}
            />
            <Button
                label={t('save')}
                icon="pi pi-check"
                onClick={handleSubmit}
                loading={submitting}
                disabled={!isValid || submitting}
            />
        </div>
    );

    return (
        <Dialog
            header={t('pages.maintenance_orders.allocate_time')}
            visible={visible}
            onHide={handleHide}
            footer={footer}
            style={{ width: '32rem' }}
            breakpoints={{ '960px': '90vw' }}
            modal
            dismissableMask={!submitting}
        >
            <div className="flex flex-column gap-3 pt-2">
                <div className="flex flex-column gap-2">
                    <label htmlFor="employee" className="font-medium text-900">
                        {t('pages.maintenance_orders.employee')}
                    </label>
                    <Dropdown
                        id="employee"
                        value={employee}
                        options={employees}
                        onChange={(e) => setEmployee(e.value)}
                        placeholder="Selecciona usuario"
                        filter
                        filterBy="label"
                        filterMatchMode="contains"
                    />
                </div>
                <div className="flex flex-column gap-2">
                    <label htmlFor="initial_date" className="font-medium text-900">
                        {t('pages.maintenance_orders.initial_date')}
                    </label>
                    <Calendar
                        id="initial_date"
                        value={initialDate}
                        onChange={(e) => setInitialDate((e.value as Date) ?? null)}
                        showTime
                        hourFormat="24"
                        showIcon
                        dateFormat="dd/mm/yy"
                    />
                </div>

                <div className="flex flex-column gap-2">
                    <label htmlFor="final_date" className="font-medium text-900">
                        {t('pages.maintenance_orders.final_date')}
                    </label>
                    <Calendar
                        id="final_date"
                        value={finalDate}
                        onChange={(e) => setFinalDate((e.value as Date) ?? null)}
                        showTime
                        hourFormat="24"
                        showIcon
                        dateFormat="dd/mm/yy"
                        minDate={initialDate ?? undefined}
                    />
                    {dateConversionError && (
                        <small className="p-error">{dateConversionError}</small>
                    )}
                </div>

                <div className="flex flex-column gap-2">
                    <label htmlFor="comments" className="font-medium text-900">
                        {t('pages.maintenance_orders.comments')}
                    </label>
                    <InputTextarea
                        id="comments"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        rows={3}
                        autoResize
                    />
                </div>

                {durationMinutes !== null && (
                    <div className="text-700 text-sm">
                        {t('pages.maintenance_orders.duration')}:{' '}
                        <span
                            className={
                                durationMinutes < 0
                                    ? 'text-red-500 font-bold'
                                    : 'text-900 font-bold'
                            }
                        >
                            {durationMinutes} min
                        </span>
                        {durationMinutes < 0 && (
                            <span className="text-red-500 ml-2">
                                {t('pages.maintenance_orders.final_before_initial')}
                            </span>
                        )}
                    </div>
                )}

                {error && <div className="text-red-500 text-sm">{error}</div>}
            </div>
        </Dialog>
    );
}

export default AllocateTimeModal;